import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAccountsStore, useAllTransactionsStore } from '@lib/store';
import { AmountUtils } from '@shared/utils';
import { type IDate, Table } from '@shared/widgets';

interface TransactionsProps {
  selectedOptions: { leftLabel: string; value: string };
  dateFilter?: IDate;
}

export const Transactions = (props: TransactionsProps) => {
  const { id } = useParams<{ id: string }>();

  const accountsStore = useAccountsStore();
  const allTransactionsStore = useAllTransactionsStore();

  const [sortOrder, setSortOrder] = useState<string>('desc');

  useEffect(() => {
    if (!id) return;
    allTransactionsStore.getAllTransactions(id);
  }, [id]);

  const columns = [
    { title: 'Date', onSort: () => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc')) },
    { title: 'DESCRIPTION' },
    { title: 'TO/FROM' },
    { title: 'PAID IN' },
    { title: 'PAID OUT' },
    { title: 'BALANCE' },
  ];
  const filteredData = useMemo(() => {
    let data = allTransactionsStore.data;

    if (props.selectedOptions?.value === 'PaidIn') data = data?.filter((row) => row.PaidIn! > 0);
    else if (props.selectedOptions?.value === 'PaidOut') data = data?.filter((row) => row.PaidOut! > 0);

    if (props.dateFilter?.from || props.dateFilter?.to) {
      data = data?.filter((row) => {
        const transactionDate = dayjs(row.Date);
        const isAfterFrom = props.dateFilter?.from ? transactionDate.isAfter(dayjs(props.dateFilter.from), 'day') : true;
        const isBeforeTo = props.dateFilter?.to ? transactionDate.isBefore(dayjs(props.dateFilter.to), 'day') : true;
        return isAfterFrom && isBeforeTo;
      });
    }

    return data;
  }, [props.selectedOptions, props.dateFilter, allTransactionsStore.data]);
  const sortedData = useMemo(() => {
    if (sortOrder === 'asc') return filteredData.slice().sort((a, b) => (dayjs(a.Date).isBefore(dayjs(b.Date)) ? -1 : 1));
    else if (sortOrder === 'desc') return filteredData.slice().sort((a, b) => (dayjs(a.Date).isBefore(dayjs(b.Date)) ? 1 : -1));
    return filteredData;
  }, [filteredData, sortOrder]);
  const currentAccount = accountsStore.data.find((item) => item.Id === id);
  return (
    <Table
      isScrollable
      loading={allTransactionsStore.loading}
      columns={columns}
      items={sortedData.map((row) => [
        { value: row.Date ? dayjs(row.Date).format('DD.MM.YYYY') : '--' },
        { value: row.Description ? row.Description : '--' },
        { value: row.FromTo ? row.FromTo : '--' },
        { value: row.PaidIn ? `${currentAccount?.Currency || ''} ${AmountUtils.transformToAmount(row.PaidIn)}` : '--' },
        { value: row.PaidOut ? `${currentAccount?.Currency || ''} ${AmountUtils.transformToAmount(row.PaidOut!)}` : '--' },
        { value: row.Balance ? `${currentAccount?.Currency || ''} ${AmountUtils.transformToAmount(row.Balance!)}` : '--' },
      ])}
      accountTab
      sortOrder={sortOrder}
    />
  );
};
